import React, { useState } from 'react';
import { X } from 'lucide-react';
import {
    ModalOverlay,
    ModalPanel,
    ModalHeader,
    ModalTitle,
    ModalSubtitle,
    ModalCloseButton,
    ModalBody,
    ModalActions,
    ModalButton,
    ModalStatusMessage,
} from './shared/Modal';

export interface FilevinePushModalProps {
    /** Enqueue the push job for the current case. ``selectedOnly`` pushes just
     *  the documents selected in the UI (only offered when a selection exists). */
    onConfirm: (opts: { selectedOnly: boolean }) => Promise<void> | void;
    onClose: () => void;
    /** Project label for context (e.g. "Smith" or "project 46774103"). */
    projectLabel?: string | null;
    /** Number of documents currently selected in the case (0 if none). */
    selectedCount?: number;
}

/**
 * "Push results back to Filevine" confirmation dialog.
 *
 * Write-back is gated OFF by default. Because the frontend can't read the
 * server flag, this modal explains BOTH outcomes plainly: with write-back
 * disabled the job writes a LOCAL preview (`pdf_processing_output/…`, no
 * upload); only when `BIGPDF_FILEVINE_PUSH_ENABLED` is set does it upload to
 * Filevine. Either way it enqueues a FILEVINE_PUSH job whose progress the
 * shared jobs indicator surfaces, so this modal just queues and closes.
 */
export const FilevinePushModal: React.FC<FilevinePushModalProps> = ({
    onConfirm,
    onClose,
    projectLabel,
    selectedCount = 0,
}) => {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Default to pushing only the selection when one exists — that's the intent
    // when a user selects docs then chooses Push. No selection => push all.
    const [selectedOnly, setSelectedOnly] = useState(selectedCount > 0);

    const handleConfirm = async () => {
        if (submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            await onConfirm({ selectedOnly: selectedCount > 0 && selectedOnly });
            onClose();
        } catch (e) {
            setError('Failed to queue the push. Please try again.');
            setSubmitting(false);
        }
    };

    return (
        <ModalOverlay onClick={onClose}>
            <ModalPanel $width="460px" onClick={e => e.stopPropagation()}>
                <ModalHeader>
                    <div>
                        <ModalTitle>Push to Filevine</ModalTitle>
                        <ModalSubtitle>
                            {projectLabel
                                ? `Send this case's documents to ${projectLabel}.`
                                : "Send this case's documents back to Filevine."}
                        </ModalSubtitle>
                    </div>
                    <ModalCloseButton onClick={onClose} aria-label="Close">
                        <X size={16} />
                    </ModalCloseButton>
                </ModalHeader>
                <ModalBody>
                    {selectedCount > 0 && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8,
                                        fontSize: 13, cursor: 'pointer', marginBottom: 4 }}>
                            <input
                                type="checkbox"
                                checked={selectedOnly}
                                onChange={e => setSelectedOnly(e.target.checked)}
                            />
                            Push only the {selectedCount} selected document
                            {selectedCount === 1 ? '' : 's'}
                        </label>
                    )}
                    <ModalStatusMessage $tone="info">
                        This exports{' '}
                        {selectedCount > 0 && selectedOnly
                            ? `the ${selectedCount} selected document${selectedCount === 1 ? '' : 's'}`
                            : 'every document in the case'}{' '}
                        as an individual PDF (plus an index), with the extracted date
                        prepended to each name and its category as a tag.
                        <br />
                        <br />
                        The files are uploaded into a new dated subfolder of the
                        project&apos;s <code>pdf_processing_output</code> folder. The
                        push only <strong>adds</strong> files — the original documents
                        in Filevine are never modified, and re-pushing after
                        corrections never overwrites an earlier export.
                    </ModalStatusMessage>
                    {error && <ModalStatusMessage $tone="error">{error}</ModalStatusMessage>}
                </ModalBody>
                <ModalActions>
                    <ModalButton $variant="secondary" onClick={onClose} disabled={submitting}>
                        Cancel
                    </ModalButton>
                    <ModalButton
                        $variant="primary"
                        onClick={() => void handleConfirm()}
                        disabled={submitting}
                    >
                        {submitting ? 'Queuing…' : 'Push to Filevine'}
                    </ModalButton>
                </ModalActions>
            </ModalPanel>
        </ModalOverlay>
    );
};
